import { useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { currencyExponent } from "@pontimed/types";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExpandableRow } from "@/components/ui/responsive-table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useAdminContext } from "@/lib/admin-context";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Consultation payments (_docs/stripe-connect-consultation-payments.md §6).
 *
 * The minimum to run online payments for video consultations:
 *   - the global switch (each country also needs Country.features.consultationPayments);
 *   - the payments, filtered by status — what needs a person: held (a
 *     reported problem, a settlement currency mismatch, a review),
 *     refund_failed, transfer_failed, disputed;
 *   - hold / release a transfer, refund everything to the patient, retry a
 *     refund that failed after Stripe accepted it.
 *
 * Endpoints: /api/admin/consultation-payments (superadmin chain). Data via
 * `useQuery` / `useInfiniteQuery` with `dbTarget` in the key so the
 * Local↔Prod toggle refetches.
 */

export const Route = createFileRoute("/_authenticated/consultation-payments")({
  component: ConsultationPaymentsPage,
});

interface AdminConsultationPayment {
  id: string;
  appointmentId: string | null;
  merchantId: string;
  countryCode: string;
  livemode: boolean;
  currency: string;
  amount: number;
  commissionAmount: number;
  stripeFeeAmount: number | null;
  status: string;
  heldReason: string | null;
  outcome: string | null;
  refundAmount: number;
  /** What actually went back so far (the ledger: refunds minus those that failed). */
  refunded: number;
  transferEligibleAt: string;
  paidAt: string | null;
  providerJoinedAt: string | null;
  problemReportedAt: string | null;
  attempts: number;
  lastError: string | null;
  createdAt: string;
}

interface ListPage {
  payments: AdminConsultationPayment[];
  nextCursor: string | null;
}

const STATUS_FILTERS = [
  "attention",
  "all",
  "paid",
  "held",
  "refund_failed",
  "transfer_failed",
  "disputed",
  "transferred",
  "refunded",
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];
const ATTENTION = ["held", "refund_failed", "transfer_failed", "disputed"];
/** Settled payments whose transfer has not left: an admin can still give the rest back. */
const REST_REFUNDABLE = ["paid", "partially_refunded", "transfer_failed", "held"];
const PAGE_SIZE = 100;

type Action = "hold" | "release" | "refund" | "retry-refund";

function money(minor: number, currency: string, locale: string): string {
  const exp = currencyExponent(currency);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  }).format(minor / 10 ** exp);
}

function StatusName({ status }: { status: string }) {
  switch (status) {
    case "creating":
    case "awaiting_payment":
      return <Trans>Awaiting payment</Trans>;
    case "expired":
      return <Trans>Expired</Trans>;
    case "paid":
      return <Trans>Paid</Trans>;
    case "held":
      return <Trans>Held</Trans>;
    case "refund_pending":
      return <Trans>Refund pending</Trans>;
    case "refund_failed":
      return <Trans>Refund failed</Trans>;
    case "partially_refunded":
      return <Trans>Partly refunded</Trans>;
    case "refunded":
      return <Trans>Refunded</Trans>;
    case "transfer_pending":
      return <Trans>Transfer pending</Trans>;
    case "transfer_failed":
      return <Trans>Transfer failed</Trans>;
    case "transferred":
      return <Trans>Transferred</Trans>;
    case "disputed":
      return <Trans>Disputed</Trans>;
    default:
      return <>{status}</>;
  }
}

function HeldReasonName({ reason }: { reason: string }) {
  switch (reason) {
    case "currency_mismatch":
      return <Trans>settlement currency</Trans>;
    case "review":
      return <Trans>manual review</Trans>;
    case "problem_reported":
      return <Trans>problem reported</Trans>;
    case "external_refund":
      return <Trans>refund made in Stripe</Trans>;
    case "admin":
      return <Trans>held by an admin</Trans>;
    default:
      return <>{reason}</>;
  }
}

function OutcomeName({ outcome }: { outcome: string | null }) {
  switch (outcome) {
    case null:
      return <>—</>;
    case "completed":
      return <Trans>Completed</Trans>;
    case "patient_early_cancel":
      return <Trans>Cancelled by the patient in time</Trans>;
    case "patient_late_cancel":
      return <Trans>Cancelled by the patient late</Trans>;
    case "provider_cancel":
      return <Trans>Cancelled by the doctor</Trans>;
    case "system_cancel":
      return <Trans>Cancelled by PontiMed</Trans>;
    default:
      return <>{outcome}</>;
  }
}

function ConsultationPaymentsPage() {
  const { t, i18n } = useLingui();
  const { dbTarget } = useAdminContext();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("attention");
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<
    | { kind: "action"; id: string; action: Action; text: string }
    | { kind: "switch"; enabled: boolean; text: string }
    | null
  >(null);

  const switchQuery = useQuery({
    queryKey: ["admin", "consultation-payments", "switch", dbTarget],
    queryFn: () => apiFetch.get<{ enabled: boolean }>("/api/admin/consultation-payments/switch"),
  });

  const listQuery = useInfiniteQuery({
    queryKey: ["admin", "consultation-payments", "list", filter, dbTarget],
    queryFn: ({ pageParam }) => {
      const statuses =
        filter === "attention" ? ATTENTION.join(",") : filter === "all" ? "" : filter;
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (statuses) params.set("status", statuses);
      if (pageParam) params.set("cursor", pageParam);
      return apiFetch.get<ListPage>(`/api/admin/consultation-payments?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 15_000,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "consultation-payments"] });

  const act = async (id: string, action: Action) => {
    setBusy(`${id}:${action}`);
    try {
      await apiFetch.post(`/api/admin/consultation-payments/${id}/${action}`, {});
      showToast(t`Done`, "success");
      await refresh();
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { code?: string })?.code : undefined;
      showToast(code ? t`Failed: ${code}` : t`Failed`, "error");
    } finally {
      setBusy(null);
    }
  };

  const toggleSwitch = async (enabled: boolean) => {
    try {
      await apiFetch.put("/api/admin/consultation-payments/switch", { enabled });
      showToast(t`Saved`, "success");
      await refresh();
    } catch {
      showToast(t`Failed`, "error");
    }
  };

  const confirmPending = () => {
    const p = pending;
    setPending(null);
    if (!p) return;
    if (p.kind === "action") void act(p.id, p.action);
    else void toggleSwitch(p.enabled);
  };

  const askAction = (id: string, action: Action, text: string) =>
    setPending({ kind: "action", id, action, text });

  const restConfirm = (p: AdminConsultationPayment) => {
    const rest = money(p.amount - p.refunded, p.currency, i18n.locale);
    return t`Refund the remaining ${rest} to the patient? The doctor gets nothing from this payment; PontiMed absorbs the card fee.`;
  };

  const statusLabel = (p: AdminConsultationPayment) => (
    <>
      <StatusName status={p.status} />
      {p.status === "held" && p.heldReason && (
        <>
          {" · "}
          <HeldReasonName reason={p.heldReason} />
        </>
      )}
    </>
  );

  const rows = listQuery.data?.pages.flatMap((page) => page.payments) ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground text-2xl font-semibold">
          <Trans>Consultation payments</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>
            Video consultations paid online: what needs a decision, and the global switch.
          </Trans>
        </p>
      </header>

      <Card>
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-foreground text-sm font-medium">
              <Trans>Online payments</Trans>
            </p>
            <p className="text-muted-foreground text-xs">
              <Trans>
                Global switch. A country also needs features.consultationPayments = true.
              </Trans>
            </p>
          </div>
          {switchQuery.isLoading ? (
            <Skeleton className="h-11 w-28" />
          ) : (
            <Button
              variant={switchQuery.data?.enabled ? "destructive" : "default"}
              className="min-h-[44px]"
              onClick={() => {
                const enabled = !switchQuery.data?.enabled;
                setPending({
                  kind: "switch",
                  enabled,
                  text: enabled
                    ? t`Turn ON online payments for video consultations? Countries also need their own switch.`
                    : t`Turn OFF online payments? New bookings stop charging at once; existing payments continue.`,
                });
              }}
            >
              {switchQuery.data?.enabled ? <Trans>Turn off</Trans> : <Trans>Turn on</Trans>}
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            className="min-h-[44px]"
            onClick={() => setFilter(f)}
          >
            {f === "attention" ? (
              <Trans>Needs attention</Trans>
            ) : f === "all" ? (
              <Trans>All</Trans>
            ) : (
              <StatusName status={f} />
            )}
          </Button>
        ))}
      </div>

      <div className="border-border overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Trans>Created</Trans>
              </TableHead>
              <TableHead>
                <Trans>Status</Trans>
              </TableHead>
              <TableHead className="text-right">
                <Trans>Amount</Trans>
              </TableHead>
              <TableHead className="hidden md:table-cell">
                <Trans>Commission</Trans>
              </TableHead>
              <TableHead className="hidden lg:table-cell">
                <Trans>Outcome</Trans>
              </TableHead>
              <TableHead className="hidden lg:table-cell">
                <Trans>Last error</Trans>
              </TableHead>
              <TableHead className="text-right">
                <Trans>Actions</Trans>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listQuery.isLoading &&
              [0, 1, 2].map((i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            {!listQuery.isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground py-8 text-center text-sm">
                  <Trans>Nothing here.</Trans>
                </TableCell>
              </TableRow>
            )}
            {rows.map((p) => (
              <ExpandableRow
                key={p.id}
                mobileDetails={[
                  {
                    label: t`Commission`,
                    value: money(p.commissionAmount, p.currency, i18n.locale),
                  },
                  { label: t`Outcome`, value: <OutcomeName outcome={p.outcome} /> },
                  { label: t`Doctor joined`, value: p.providerJoinedAt ? t`yes` : t`no` },
                  { label: t`Last error`, value: p.lastError ?? "—" },
                  { label: t`ID`, value: p.id },
                ]}
              >
                <TableCell className="text-xs whitespace-nowrap">
                  {new Date(p.createdAt).toLocaleString(i18n.locale)}
                  {!p.livemode && (
                    <Badge variant="outline" className="ml-2">
                      <Trans>test</Trans>
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs">{statusLabel(p)}</TableCell>
                <TableCell className="text-right text-xs tabular-nums">
                  {money(p.amount, p.currency, i18n.locale)}
                  {p.refundAmount > 0 && (
                    <span className="text-muted-foreground block">
                      −{money(p.refundAmount, p.currency, i18n.locale)}
                    </span>
                  )}
                </TableCell>
                <TableCell className="hidden text-xs tabular-nums md:table-cell">
                  {money(p.commissionAmount, p.currency, i18n.locale)}
                </TableCell>
                <TableCell className="hidden text-xs lg:table-cell">
                  <OutcomeName outcome={p.outcome} />
                </TableCell>
                <TableCell className="text-muted-foreground hidden max-w-56 truncate text-xs lg:table-cell">
                  {p.lastError ?? "—"}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-wrap justify-end gap-1">
                    {["paid", "partially_refunded", "transfer_failed"].includes(p.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px]"
                        disabled={busy !== null}
                        onClick={() =>
                          askAction(p.id, "hold", t`Hold this payment's transfer to the doctor?`)
                        }
                      >
                        <Trans>Hold</Trans>
                      </Button>
                    )}
                    {p.status === "held" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px]"
                        disabled={busy !== null}
                        onClick={() =>
                          askAction(p.id, "release", t`Release the hold? The transfer goes ahead.`)
                        }
                      >
                        <Trans>Release</Trans>
                      </Button>
                    )}
                    {(p.status === "paid" || p.status === "held") && !p.outcome && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="min-h-[44px]"
                        disabled={busy !== null}
                        onClick={() =>
                          askAction(
                            p.id,
                            "refund",
                            t`Refund everything to the patient? PontiMed absorbs the card fee; the appointment is cancelled.`,
                          )
                        }
                      >
                        <Trans>Refund</Trans>
                      </Button>
                    )}
                    {p.outcome && REST_REFUNDABLE.includes(p.status) && p.amount > p.refunded && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="min-h-[44px]"
                        disabled={busy !== null}
                        onClick={() => askAction(p.id, "refund", restConfirm(p))}
                      >
                        <Trans>Refund the rest</Trans>
                      </Button>
                    )}
                    {p.status === "refund_failed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px]"
                        disabled={busy !== null}
                        onClick={() =>
                          askAction(p.id, "retry-refund", t`Try the failed refund again?`)
                        }
                      >
                        <Trans>Retry refund</Trans>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </ExpandableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {listQuery.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            className="min-h-[44px]"
            disabled={listQuery.isFetchingNextPage}
            onClick={() => void listQuery.fetchNextPage()}
          >
            <Trans>Load more</Trans>
          </Button>
        </div>
      )}

      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Trans>Are you sure?</Trans>
            </DialogTitle>
            <DialogDescription>{pending?.text}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setPending(null)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              variant={
                pending?.kind === "action" && pending.action === "refund"
                  ? "destructive"
                  : "default"
              }
              className="min-h-[44px]"
              onClick={confirmPending}
            >
              <Trans>Confirm</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
